/**
 * DiscordConnectModal — paste-token form for the Discord connector.
 *
 * Discord connects with TWO values (no consumer OAuth flow here):
 *   - guildId (REQUIRED) — the server the bot/Copilot operates in. Enable
 *     Developer Mode in Discord → right-click your server → Copy Server ID.
 *   - botToken (OPTIONAL) — needed for agents to POST messages; the /zibby
 *     Copilot command works without it. Discord developer portal → Bot →
 *     Reset Token. Omitted from the request body entirely when blank so we
 *     never overwrite a stored token with an empty string.
 *
 * Backend validates (401 bad token, 400 bad guildId) before persisting —
 * structured error surfaces on err.message. Mirrors CircleCIConnectModal
 * for the shared shape; just two fields.
 */

import React, { useEffect, useState } from 'react';
import { IonModal, IonButton, IonSpinner } from '@ionic/react';
import { useConnectDiscord } from '../../hooks/useIntegrations';
import '../../pages/IntegrationsPage/IntegrationsPage.css';

export default function DiscordConnectModal({ isOpen, onClose, onConnected }) {
  const [guildId, setGuildId] = useState('');
  const [botToken, setBotToken] = useState('');
  const [error, setError] = useState('');
  const [connecting, setConnecting] = useState(false);
  const mutation = useConnectDiscord();

  useEffect(() => {
    if (isOpen) {
      setGuildId('');
      setBotToken('');
      setError('');
      setConnecting(false);
    }
  }, [isOpen]);

  const handleSubmit = () => {
    if (!guildId.trim()) {
      setError('Server ID (guild) is required.');
      return;
    }
    setConnecting(true);
    setError('');
    mutation.mutate(
      { guildId: guildId.trim(), ...(botToken.trim() ? { botToken: botToken.trim() } : {}) },
      {
        onSuccess: () => {
          setConnecting(false);
          onConnected?.();
          onClose?.();
        },
        onError: (err) => {
          setConnecting(false);
          setError(err?.message || 'Failed to connect Discord. Please check your server ID and bot token.');
        },
      },
    );
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={() => onClose?.()}
      className="lark-connect-modal"
      aria-label="Connect Discord"
    >
      <div
        className="lark-modal-content"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2>Connect Discord</h2>
        <p className="lark-modal-description">
          Connect a Discord server so the Teamy Copilot and agents can operate
          in it. Enable Developer Mode in Discord (User Settings → Advanced),
          then right-click your server → Copy Server ID and paste it below.
        </p>

        <div className="lark-form-group">
          <label className="lark-form-label" htmlFor="discord-guild-id">Server ID (guild)</label>
          <input
            id="discord-guild-id"
            type="text"
            value={guildId}
            placeholder="e.g. 1234567890123456789"
            onChange={(e) => { setGuildId(e.target.value || ''); setError(''); }}
            className="lark-form-input"
            disabled={connecting}
            autoComplete="off"
            spellCheck={false}
            aria-invalid={error ? 'true' : 'false'}
          />
        </div>

        <div className="lark-form-group">
          <label className="lark-form-label" htmlFor="discord-bot-token">Bot token (optional)</label>
          <input
            id="discord-bot-token"
            type="password"
            value={botToken}
            placeholder="Bot token from the Discord developer portal"
            onChange={(e) => { setBotToken(e.target.value || ''); setError(''); }}
            className="lark-form-input"
            disabled={connecting}
            autoComplete="off"
            spellCheck={false}
          />
          <p className="lark-modal-description" style={{ marginTop: 6, marginBottom: 0, fontSize: 12 }}>
            Needed for agents to post messages; the /zibby Copilot command
            works without it. From the Discord developer portal → Bot →
            Reset Token.
          </p>
        </div>

        {error && <p className="lark-modal-error" role="alert">{error}</p>}

        <div className="lark-modal-buttons">
          <IonButton
            expand="block"
            onClick={handleSubmit}
            disabled={connecting || !guildId.trim()}
            aria-label="Submit Discord credentials"
          >
            {connecting ? <IonSpinner name="crescent" /> : 'Connect'}
          </IonButton>
          <IonButton
            expand="block"
            fill="clear"
            onClick={() => onClose?.()}
            disabled={connecting}
            aria-label="Cancel Discord connection"
          >
            Cancel
          </IonButton>
        </div>
      </div>
    </IonModal>
  );
}
